import type { Client } from '@/types/client';

export type MacroGoalNutrient = 'protein' | 'carbohydrates' | 'totalFat' | 'dietaryFibre';

const getConditionSearchText = (client?: Client): string => {
  if (!client) return '';

  const conditionDetails = Array.isArray(client.medicalConditionDetails)
    ? client.medicalConditionDetails.filter(
        (condition) => String(condition.status ?? '').trim().toLowerCase() !== 'resolved',
      )
    : [];

  return [
    client.medicalConditions,
    ...conditionDetails.flatMap((condition) => [
      condition.conditionId,
      condition.conditionName,
      condition.conditionSubtype,
      ...(condition.questionAnswers ?? []).flatMap((answer) => [
        answer.field,
        typeof answer.value === 'string' ? answer.value : '',
      ]),
    ]),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
};

export const getMacroGoalHint = (client: Client | undefined, nutrient: MacroGoalNutrient): string => {
  const conditions = getConditionSearchText(client);
  const hasType1Diabetes = /\b(type\s*[-_]?\s*1|type\s+i|t1d|type1|lada)\b/.test(conditions);
  const hasDiabetes = /\b(diabetes|diabetic|prediabetes|insulin resistance)\b/.test(conditions);
  const hasIbs = /\b(ibs|irritable bowel syndrome)\b/.test(conditions);
  const hasKidneyDisease = /\b(kidney disease|renal disease|chronic kidney|ckd)\b/.test(conditions);
  const hasCardiovascularCondition =
    /\b(heart disease|cardiovascular|high cholesterol|hyperlipid|coronary)\b/.test(conditions);

  if (nutrient === 'protein') {
    return hasKidneyDisease
      ? 'Kidney disease hint: individualize protein for kidney function and the client\'s clinical plan.'
      : 'Hint: protein provides 4 kcal/g. Individualize the goal for body weight, activity, and clinical needs.';
  }

  if (nutrient === 'carbohydrates') {
    if (hasType1Diabetes) {
      return 'T1D hint: carbohydrate is recommended not to be set below 50 g/day unless clinically indicated. Keep intake consistent with the insulin plan.';
    }
    if (hasDiabetes) {
      return 'Diabetes hint: favor consistent, fibre-rich carbohydrate portions distributed across the day.';
    }
    return 'Hint: carbohydrate provides 4 kcal/g. Favor consistent portions and fibre-rich sources.';
  }

  if (nutrient === 'totalFat') {
    return hasCardiovascularCondition
      ? 'Cardiovascular hint: prioritize unsaturated fats and keep saturated and trans fats limited.'
      : 'Hint: total fat provides 9 kcal/g. Favor mostly unsaturated fat sources.';
  }

  return hasIbs
    ? 'IBS hint: at least 25 g fibre/day is recommended, introduced gradually and adjusted for subtype and tolerance.'
    : 'Hint: fibre provides 2 kcal/g. Increase gradually and pair with adequate fluids.';
};
